/*
 * Copyright (c) 2016-2025 Martin Donath <martin.donath@squidfunk.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import {
  Observable,
  Subject,
  animationFrameScheduler,
  asyncScheduler,
  auditTime,
  combineLatest,
  defer,
  distinctUntilChanged,
  endWith,
  finalize,
  first,
  from,
  fromEvent,
  ignoreElements,
  map,
  mergeMap,
  observeOn,
  takeUntil,
  tap,
  withLatestFrom
} from "rxjs"

import { feature } from "~/_"
import {
  Viewport,
  getElement,
  getElementOffset,
  getElements
} from "~/browser"

import { Component } from "../_"
import { Header } from "../header"
import { Main } from "../main"
import { mountInlineTooltip2 } from "../tooltip2"

/* ----------------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------------- */

/**
 * Sidebar
 */
export interface Sidebar {
  height: number                       /* Sidebar height */
  locked: boolean                      /* Sidebar is locked */
}

/* ----------------------------------------------------------------------------
 * Helper types
 * ------------------------------------------------------------------------- */

/**
 * Watch options
 */
interface WatchOptions {
  viewport$: Observable<Viewport>      /* Viewport observable */
  main$: Observable<Main>              /* Main area observable */
}

/**
 * Mount options
 */
interface MountOptions {
  viewport$: Observable<Viewport>      /* Viewport observable */
  header$: Observable<Header>          /* Header observable */
  main$: Observable<Main>              /* Main area observable */
}

/* ----------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------- */

/**
 * Media query that matches the desktop breakpoint at which the primary sidebar
 * stops being a drawer and becomes a sticky column.
 */
const DESKTOP_BREAKPOINT = "(min-width: 87.5em)"

/**
 * Storage keys used to persist sidebar scroll position across navigations.
 */
const SCROLL_STORAGE_KEY_PRIMARY = "cc-sidebar-scroll-primary"
const SCROLL_STORAGE_KEY_SECONDARY = "cc-sidebar-scroll-secondary"

/* ----------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------- */

/**
 * Whether the current viewport uses the desktop sidebar layout.
 */
function isDesktopLayout(): boolean {
  return window.matchMedia(DESKTOP_BREAKPOINT).matches
}

/**
 * Compute the storage key for a sidebar element.
 */
function getSidebarStorageKey(el: HTMLElement): string {
  return el.classList.contains("md-sidebar--secondary")
    ? SCROLL_STORAGE_KEY_SECONDARY
    : SCROLL_STORAGE_KEY_PRIMARY
}

/**
 * Whether an element is fully visible inside its scrollable container.
 */
function isItemVisibleInContainer(
  item: HTMLElement,
  container: HTMLElement
): boolean {
  const containerRect = container.getBoundingClientRect()
  const itemRect = item.getBoundingClientRect()

  const relativeTop = itemRect.top - containerRect.top + container.scrollTop
  const relativeBottom = relativeTop + itemRect.height

  const viewTop = container.scrollTop
  const viewBottom = viewTop + container.clientHeight

  return relativeTop >= viewTop && relativeBottom <= viewBottom
}

/**
 * Center the active nav item inside the given scrollable container.
 */
function centerItemInContainer(
  item: HTMLElement,
  container: HTMLElement
): void {
  const containerRect = container.getBoundingClientRect()
  const itemRect = item.getBoundingClientRect()

  const relativeTop = itemRect.top - containerRect.top + container.scrollTop
  const desiredScroll =
    relativeTop - (container.clientHeight - itemRect.height) / 2

  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight)
  container.scrollTop = Math.max(0, Math.min(desiredScroll, maxScroll))
}

/* ----------------------------------------------------------------------------
 * Functions
 * ------------------------------------------------------------------------- */

/**
 * Watch sidebar
 *
 * This function returns an observable that computes the visual parameters of
 * the sidebar which depends on the vertical viewport offset, as well as the
 * height of the main area. When the page is scrolled beyond the header, the
 * sidebar is locked and fills the remaining space.
 *
 * @param el - Sidebar element
 * @param options - Options
 *
 * @returns Sidebar observable
 */
export function watchSidebar(
  el: HTMLElement, { viewport$, main$ }: WatchOptions
): Observable<Sidebar> {
  const parent = el.closest<HTMLElement>(".md-grid")!
  const adjust =
    parent.offsetTop -
    parent.parentElement!.offsetTop

  /* Compute the sidebar's available height and if it should be locked */
  return combineLatest([main$, viewport$])
    .pipe(
      map(([{ offset, height }, { offset: { y } }]) => {
        height = height
          + Math.min(adjust, Math.max(0, y - offset))
          - adjust
        return {
          height,
          locked: y >= offset + adjust
        }
      }),
      distinctUntilChanged((a, b) => (
        a.height === b.height &&
        a.locked === b.locked
      ))
    )
}

/**
 * Mount sidebar
 *
 * This function doesn't set the height of the actual sidebar, but of its first
 * child – the `.md-sidebar__scrollwrap` element in order to mitigate jittery
 * sidebars when the footer is scrolled into view. At some point we switched
 * from `absolute` / `fixed` positioning to `sticky` positioning, significantly
 * reducing jitter in some browsers (respectively Firefox and Safari) when
 * scrolling from the top. However, top-aligned sticky positioning means that
 * the sidebar snaps to the bottom when the end of the container is reached.
 * This is what leads to the mentioned jitter, as the sidebar's height may be
 * updated too slowly.
 *
 * This behaviour can be mitigated by setting the height of the sidebar to `0`
 * while preserving the padding, and the height on its first element.
 *
 * Sticky-layout geometry only applies on desktop. On mobile the primary
 * sidebar is rendered as a drawer (position: fixed, full viewport height),
 * and any inline height/top would conflict with that. Geometry styles are
 * therefore cleared on mobile.
 *
 * @param el - Sidebar element
 * @param options - Options
 *
 * @returns Sidebar component observable
 */
export function mountSidebar(
  el: HTMLElement, { header$, ...options }: MountOptions
): Observable<Component<Sidebar>> {
  const inner = getElement(".md-sidebar__scrollwrap", el)
  const { y } = getElementOffset(inner)

  return defer(() => {
    const push$ = new Subject<Sidebar>()
    const done$ = push$.pipe(ignoreElements(), endWith(true))
    const next$ = push$.pipe(auditTime(0, animationFrameScheduler))

    /* Update sidebar height and offset (desktop sticky layout only) */
    next$.pipe(withLatestFrom(header$))
      .subscribe({
        next([{ height }, { height: offset }]) {
          if (isDesktopLayout()) {
            inner.style.height = `${height - 2 * y}px`
            el.style.top = `${offset}px`
          } else {
            inner.style.height = ""
            el.style.top = ""
          }
        },

        complete() {
          inner.style.height = ""
          el.style.top = ""
        }
      })

    /* Restore sidebar scroll position or bring active item into view */
    next$.pipe(first())
      .subscribe(() => {
        const container = getElement<HTMLElement>(".md-sidebar__scrollwrap", el)
        if (!container)
          return

        const sidebarKey = getSidebarStorageKey(el)
        restoreSidebarScroll(el, container, sidebarKey)

        /* Save scroll position on user scroll, debounced to avoid excess
           sessionStorage writes during momentum scrolling */
        fromEvent(container, "scroll")
          .pipe(
            auditTime(150, asyncScheduler),
            takeUntil(done$)
          )
          .subscribe(() => {
            sessionStorage.setItem(sidebarKey, String(container.scrollTop))
          })

        /* Force-save scroll position on link click before navigation starts.
           Without this, the debounced scroll listener may save a stale value
           if the user scrolled manually then immediately clicked a link */
        fromEvent(el, "click")
          .pipe(takeUntil(done$))
          .subscribe(event => {
            const target = event.target as HTMLElement
            if (target.closest("a.md-nav__link"))
              sessionStorage.setItem(sidebarKey, String(container.scrollTop))
          })
      })

    /* Mobile: scroll active item into view when the drawer opens.
       The drawer toggle lives outside the sidebar element, so we look it up
       on the document. We use requestAnimationFrame rather than waiting for
       transitionend so the scroll happens before the drawer becomes visible,
       avoiding a visible jump from the top of the list to the active item */
    if (el.classList.contains("md-sidebar--primary")) {
      const drawer = el.ownerDocument
        .querySelector<HTMLInputElement>("[data-md-toggle='drawer']")

      if (drawer) {
        fromEvent(drawer, "change")
          .pipe(takeUntil(done$))
          .subscribe(() => {
            if (!drawer.checked || isDesktopLayout())
              return

            requestAnimationFrame(() => {
              scrollMobileDrawerToActive(el)
            })
          })
      }
    }

    /* Handle accessibility for expandable items, see https://bit.ly/3jaod9p */
    from(getElements<HTMLLabelElement>("label[tabindex]", el))
      .pipe(
        mergeMap(label => fromEvent(label, "click")
          .pipe(
            observeOn(asyncScheduler),
            map(() => label),
            takeUntil(done$)
          )
        )
      )
      .subscribe(label => {
        const doc = label.ownerDocument
        const input = doc.querySelector<HTMLInputElement>(`[id="${label.htmlFor}"]`)
        const nav = doc.querySelector<HTMLElement>(`[aria-labelledby="${label.id}"]`)

        if (!input || !nav)
          return

        nav.setAttribute("aria-expanded", `${input.checked}`)
      })

    /* Mount abbreviation tooltips */
    if (feature("content.tooltips"))
      from(getElements("abbr[title]", el))
        .pipe(
          mergeMap(child => mountInlineTooltip2(child, { viewport$ })),
          takeUntil(done$)
        )
        .subscribe()

    /* Create and return component */
    return watchSidebar(el, options)
      .pipe(
        tap(state => push$.next(state)),
        finalize(() => push$.complete()),
        map(state => ({ ref: el, ...state }))
      )
  })
}

/* ----------------------------------------------------------------------------
 * Scroll restoration
 * ------------------------------------------------------------------------- */

/**
 * Restore the sidebar's previous scroll position from sessionStorage. If the
 * restored position doesn't show the active item (e.g. user navigated to a
 * different section), fall back to centering the active item in view.
 */
function restoreSidebarScroll(
  el: HTMLElement,
  container: HTMLElement,
  sidebarKey: string
): void {
  let restored = false

  const saved = sessionStorage.getItem(sidebarKey)
  if (saved !== null) {
    const savedScroll = parseInt(saved, 10)
    if (!isNaN(savedScroll)) {
      const maxScroll = Math.max(
        0,
        container.scrollHeight - container.clientHeight
      )
      container.scrollTop = Math.max(0, Math.min(savedScroll, maxScroll))

      const activeItem = container.querySelector<HTMLElement>(
        ".md-nav__link--active[href]"
      )

      /* If the restored scroll position already shows the active item, or
         there's no active item to worry about, accept the restored value */
      if (!activeItem || !activeItem.clientHeight) {
        restored = true
      } else if (isItemVisibleInContainer(activeItem, container)) {
        restored = true
      }
    }
  }

  if (restored)
    return

  /* Fallback: center the active item in its scrollable container */
  for (const item of getElements<HTMLElement>(
    ".md-nav__link--active[href]",
    el
  )) {
    if (!item.clientHeight)
      continue

    const itemContainer = item.closest<HTMLElement>(".md-sidebar__scrollwrap")
    if (!itemContainer)
      continue

    if (isItemVisibleInContainer(item, itemContainer))
      continue

    centerItemInContainer(item, itemContainer)
  }
}

/* ----------------------------------------------------------------------------
 * Mobile drawer scroll alignment
 * ------------------------------------------------------------------------- */

/**
 * On mobile, the primary sidebar drawer renders the active drill-down level
 * inside a scrollable `.md-nav__list`. Different lists become scrollable at
 * different times depending on which level the user is viewing — only the
 * currently visible list has overflow.
 *
 * This function walks up from the deepest active item to find the nearest
 * ancestor list that actually overflows, and scrolls the active item into
 * the center of that list.
 */
function scrollMobileDrawerToActive(el: HTMLElement): void {
  const items = el.querySelectorAll<HTMLElement>(
    ".md-nav--primary .md-nav__item--active"
  )
  if (!items.length)
    return

  const target = items[items.length - 1]

  let container: HTMLElement | null = target.parentElement
  while (container) {
    if (
      container.classList.contains("md-nav__list") &&
      container.scrollHeight > container.clientHeight
    ) {
      break
    }
    container = container.parentElement
  }

  if (!container)
    return

  centerItemInContainer(target, container)
}